import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  notifyHomologationItemLoadFailure,
  takeHomologationItem,
  type DocumentRevision,
} from '@/entities/Document';
import api from '@/shared/api';
import {
  buildHomologationListRoute,
  formatInstantToPtBR,
  formatCivilDateToPtBR,
  formatCollaboratorNameAndRegistration,
  type ApiResponse,
  type InfoDocumento,
} from '@/shared/lib';

import {
  FALLBACK_POSITION,
  FALLBACK_USER_NAME,
  VERIFICATION_ITEM_MESSAGES,
} from '../config/messages';

interface TrainingState {
  required: boolean;
  justification: string | null;
}

interface PositionById {
  positionId: number;
  positionName: string;
}

interface UserLookupResult {
  name: string;
  position: string;
}

type VerificationTrainingSession = NonNullable<
  NonNullable<DocumentRevision['trainingDetails']>['sessions']
>[number];

type VerificationMember = NonNullable<
  NonNullable<DocumentRevision['trainingDetails']>['members']
>[number];

interface LegacyVerificationUser {
  id: number;
  name?: string;
  memberships?: {
    position?: {
      name?: string;
    };
  }[];
}

type VerificationCollaborator = VerificationMember | LegacyVerificationUser;

interface SelectOption {
  id: string;
  name: string;
}

interface UseVerificationIdOptions {
  redirectOnErrorTo?: string | null;
}

interface CollaboratorViewData {
  id: number;
  name: string;
  position: string;
}

type InstructorInfo = InfoDocumento[];

function formatRevisionLabel(revision?: number | null): string {
  return revision != null ? `Rev. ${String(revision).padStart(2, '0')}` : '';
}

function isVerificationMember(
  collaborator: VerificationCollaborator
): collaborator is VerificationMember {
  return 'memberId' in collaborator;
}

export function useVerificationId(options: UseVerificationIdOptions = {}) {
  const router = useRouter();
  const { redirectOnErrorTo = '/verificacao' } = options;

  const verification = ref<DocumentRevision | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const userDirectory = ref<Map<number, UserLookupResult>>(new Map());

  const getTrainingMembers = (): VerificationMember[] =>
    verification.value?.trainingDetails?.members ?? [];

  const getTrainingCollaborators = (): VerificationCollaborator[] => {
    const members = getTrainingMembers();
    if (members.length) {
      return members;
    }

    const legacyUsers =
      (verification.value?.trainingDetails as { users?: LegacyVerificationUser[] } | undefined)
        ?.users ?? [];

    return legacyUsers;
  };

  const resolveSessionUserId = (session: VerificationTrainingSession): number | null => {
    if (typeof session.responsibleUserId === 'number') {
      return session.responsibleUserId;
    }

    if (typeof session.responsibleMemberId !== 'number') {
      return null;
    }

    const member = getTrainingMembers().find(
      (item) => item.memberId === session.responsibleMemberId
    );
    return member?.user.id ?? null;
  };

  const lookupUserData = (userId: number): UserLookupResult =>
    userDirectory.value.get(userId) ?? {
      name: FALLBACK_USER_NAME(userId),
      position: FALLBACK_POSITION,
    };

  const resolveSessionPositionLabel = (
    session: VerificationTrainingSession,
    userId: number
  ): string => {
    if (typeof session.responsibleUserPositionId === 'number') {
      const position = verification.value?.positions?.find(
        (item) => item.id === session.responsibleUserPositionId
      );

      if (position?.name) {
        return position.name;
      }
    }

    return lookupUserData(userId).position;
  };

  const revisionLabel = computed(() => formatRevisionLabel(verification.value?.revision));

  const documentElaborationInfo = computed<InfoDocumento[]>(() => [
    {
      label: 'Data da solicitação',
      value: formatInstantToPtBR(verification.value?.createdAt),
    },
    {
      label: 'Prefixo',
      value: verification.value?.document?.prefix ?? '',
    },
    {
      label: 'Revisão',
      value: formatRevisionLabel(verification.value?.revision),
    },
    {
      label: 'Elaboração',
      value: formatCollaboratorNameAndRegistration(
        verification.value?.document?.user?.name,
        verification.value?.document?.user?.registrationNumber
      ),
    },
  ]);

  const documentApprovalInfo = computed<InfoDocumento[]>(() => [
    {
      label: 'Data da Aprovação',
      value: formatInstantToPtBR(verification.value?.approval?.approvedAt),
    },
    {
      label: 'Prefixo',
      value: verification.value?.document?.prefix ?? '',
    },
    {
      label: 'Revisão',
      value: formatRevisionLabel(verification.value?.revision),
    },
    {
      label: 'Aprovação',
      value: formatCollaboratorNameAndRegistration(
        verification.value?.approval?.approvedBy?.name,
        verification.value?.approval?.approvedBy?.registrationNumber
      ),
    },
  ]);

  const trainingInfo = computed<TrainingState>(() => ({
    required: verification.value?.training?.programmed ?? false,
    justification: verification.value?.training?.trainingJustification ?? null,
  }));

  const changelogLines = computed(
    () => verification.value?.changelogEntries?.map((entry) => entry.description) ?? []
  );

  const positionsAsSelectOptions = computed<SelectOption[]>(
    () =>
      verification.value?.positions?.map((position) => ({
        id: String(position.id),
        name: position.name,
      })) ?? []
  );

  const instructorsInfo = computed<InstructorInfo[]>(() => {
    const sessions = verification.value?.trainingDetails?.sessions;
    if (!sessions?.length) {
      return [];
    }

    return sessions.map((session) => {
      const userId = resolveSessionUserId(session);
      const user = userId ? lookupUserData(userId) : null;

      return [
        {
          label: 'Ministrante:',
          value: user?.name ?? 'Não informado',
        },
        {
          label: 'Cargo:',
          value: userId ? resolveSessionPositionLabel(session, userId) : 'Não informado',
        },
        {
          label: 'Data do treinamento:',
          value: formatCivilDateToPtBR(session.trainingDate),
        },
      ];
    });
  });

  const observationInfo = computed(() => {
    const notes = verification.value?.trainingDetails?.notes;
    return notes ? [notes] : ['Nenhuma observação registrada'];
  });

  const selectedCollaborators = computed<CollaboratorViewData[]>(() => {
    const collaborators = getTrainingCollaborators();
    if (!collaborators.length) {
      return [];
    }

    return collaborators.map((collaborator) => {
      if (isVerificationMember(collaborator)) {
        return {
          id: collaborator.user.id,
          name: collaborator.user.name ?? FALLBACK_USER_NAME(collaborator.user.id),
          position: collaborator.position?.name ?? FALLBACK_POSITION,
        };
      }

      const data = lookupUserData(collaborator.id);
      return {
        id: collaborator.id,
        name: data.name,
        position: collaborator.memberships?.[0]?.position?.name ?? data.position,
      };
    });
  });

  const fetchUserData = async (userId: number): Promise<UserLookupResult> => {
    try {
      const [userResponse, positionResponse] = await Promise.all([
        api.get<ApiResponse<{ name: string | null }>>(`/users/${userId}`),
        api.get<ApiResponse<PositionById[]>>(`/positions/user/${userId}`),
      ]);

      const positionName =
        positionResponse.data.success && positionResponse.data.data?.length
          ? positionResponse.data.data[0].positionName
          : FALLBACK_POSITION;

      return {
        name:
          userResponse.data.success && userResponse.data.data?.name
            ? userResponse.data.data.name
            : FALLBACK_USER_NAME(userId),
        position: positionName,
      };
    } catch {
      return {
        name: FALLBACK_USER_NAME(userId),
        position: FALLBACK_POSITION,
      };
    }
  };

  const collectUserIds = (
    sessions: VerificationTrainingSession[] = [],
    collaborators: VerificationCollaborator[] = []
  ): number[] => {
    const ids = new Set<number>();

    for (const session of sessions) {
      const userId = resolveSessionUserId(session);
      if (typeof userId === 'number') {
        ids.add(userId);
      }
    }

    for (const collaborator of collaborators) {
      const userId = isVerificationMember(collaborator) ? collaborator.user.id : collaborator.id;
      if (typeof userId === 'number') {
        ids.add(userId);
      }
    }

    return [...ids];
  };

  const warmUserDirectory = async (
    sessions: VerificationTrainingSession[] = [],
    collaborators: VerificationCollaborator[] = []
  ) => {
    const userIds = collectUserIds(sessions, collaborators);
    const missingUserIds = userIds.filter((userId) => !userDirectory.value.has(userId));

    if (!missingUserIds.length) {
      return;
    }

    const entries = await Promise.all(
      missingUserIds.map(async (userId) => [userId, await fetchUserData(userId)] as const)
    );

    for (const [userId, userData] of entries) {
      userDirectory.value.set(userId, userData);
    }
  };

  const fetchVerificationById = async (revisionId: number, isRecord = false) => {
    loading.value = true;
    error.value = null;

    try {
      // A natureza vem do segmento da rota, e nao de uma tentativa: o mesmo id existe nas duas
      // tabelas, entao pedir uma fonte e cair para a outra abriria o item errado.
      const response = await takeHomologationItem('verification', revisionId, isRecord);

      if (response.success && response.data) {
        verification.value = response.data;
        await warmUserDirectory(
          response.data.trainingDetails?.sessions ?? [],
          getTrainingCollaborators()
        );
        return;
      }

      verification.value = null;
      error.value = 'Não foi possível carregar os detalhes da verificação';
    } catch (fetchError) {
      verification.value = null;
      error.value = 'Erro ao carregar verificação';
      if (redirectOnErrorTo) {
        notifyHomologationItemLoadFailure(fetchError, isRecord, VERIFICATION_ITEM_MESSAGES);
        router.push(buildHomologationListRoute(redirectOnErrorTo, isRecord));
      }
    } finally {
      loading.value = false;
    }
  };

  return {
    loading,
    error,
    verification,
    trainingInfo,
    positionsAsSelectOptions,
    documentApprovalInfo,
    instructorsInfo,
    observationInfo,
    changelogLines,
    documentElaborationInfo,
    fetchVerificationById,
    selectedCollaborators,
    revisionLabel,
  };
}
